/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import ShipmentService from '../services/ShipmentService'
import Swal from 'sweetalert2'

const ShipmentList = () => {
    const { currentUser } = useContext(AppContext)
    const [shipments, setShipments] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [filterStatus, setFilterStatus] = useState('all')
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
        from: 0,
        to: 0
    })

    useEffect(() => {
        fetchShipments()
    }, [currentPage, searchTerm, filterStatus, itemsPerPage])

    const fetchShipments = async () => {
        setLoading(true)
        
        try {
            const params = {
                offset: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
                globalSearch: searchTerm,
                status: filterStatus !== 'all' ? filterStatus : '',
                orderBy: 'salesShipmentId',
                orderType: 'desc'
            }

            const response = await ShipmentService.getShipmentsList(params)
            
            if (response.success) {
                setShipments(response.data || [])
                setPagination({
                    current_page: currentPage,
                    per_page: itemsPerPage,
                    total: response.count || 0,
                    total_pages: Math.ceil((response.count || 0) / itemsPerPage),
                    has_next_page: currentPage < Math.ceil((response.count || 0) / itemsPerPage),
                    has_prev_page: currentPage > 1,
                    from: (currentPage - 1) * itemsPerPage + 1,
                    to: Math.min(currentPage * itemsPerPage, response.count || 0)
                })
            } else {
                console.error('Failed to fetch shipments:', response.errorMessage)
                setShipments([])
            }
        } catch (error) {
            console.error('Error fetching shipments:', error)
            setShipments([])
        } finally {
            setLoading(false)
        }
    }

    const handleSearchChange = (value) => {
        setSearchTerm(value)
        setCurrentPage(1)
    }

    const handleFilterChange = (type, value) => {
        if (type === 'status') {
            setFilterStatus(value)
        } else if (type === 'perPage') {
            setItemsPerPage(parseInt(value))
        }
        setCurrentPage(1)
    }

    const handlePageChange = (page) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const getStatusBadge = (status) => {
        const statusInfo = ShipmentService.formatShipmentStatus(status)
        return `badge-${statusInfo.color}`
    }

    const formatDate = (dateString) => {
        return ShipmentService.formatDate(dateString)
    }

    const handlePrintShipment = async (shipmentId) => {
        try {
            const response = await ShipmentService.getShipmentPrint(shipmentId)
            if (response.success) {
                // Open print window with shipment data
                const printWindow = window.open('', '_blank')
                printWindow.document.write(`
                    <html>
                        <head>
                            <title>Shipment Print</title>
                            <style>
                                body { font-family: Arial, sans-serif; margin: 20px; }
                                .header { text-align: center; margin-bottom: 30px; }
                                .shipment-info { margin-bottom: 20px; }
                                .address-section { margin-bottom: 20px; }
                                .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                                .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                                .totals { text-align: right; font-weight: bold; }
                            </style>
                        </head>
                        <body>
                            <div class="header">
                                <h1>Shipment Details</h1>
                                <p>Shipment No: ${response.data.shipmentNo}</p>
                                <p>Date: ${response.data.shipmentDate}</p>
                            </div>
                            <div class="shipment-info">
                                <h3>Order Information</h3>
                                <p><strong>Order No:</strong> ${response.data.order?.orderNo}</p>
                                <p><strong>Customer:</strong> ${response.data.order?.customer?.fullName}</p>
                                <p><strong>Total Qty:</strong> ${response.data.totalQty}</p>
                            </div>
                            <div class="address-section">
                                <h3>Shipping Address</h3>
                                <p>${response.data.salesShippingAddress?.fullName}</p>
                                <p>${response.data.salesShippingAddress?.street}</p>
                                <p>${response.data.salesShippingAddress?.city}, ${response.data.salesShippingAddress?.region}</p>
                                <p>Phone: ${response.data.salesShippingAddress?.phoneNumber}</p>
                            </div>
                            <table class="items-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th>Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${response.data.salesShipmentItems?.map(item => `
                                        <tr>
                                            <td>${item.product?.name}</td>
                                            <td>${item.product?.sku}</td>
                                            <td>${item.qty}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                            <div class="totals">
                                <p>Total Items: ${response.data.salesShipmentItems?.length || 0}</p>
                                <p>Total Quantity: ${response.data.totalQty}</p>
                            </div>
                        </body>
                    </html>
                `)
                printWindow.document.close()
                printWindow.print()
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load shipment data for printing',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error printing shipment:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error loading shipment data for printing',
                confirmButtonColor: '#d33'
            })
        }
    }

    return (
        <>
            <div className='card card-custom'>
                <div className='card-header card-header-mobile'>
                    <div className='card-title'>
                        <h3 className='card-label'>Shipment Management</h3>
                    </div>
                    <div className='card-toolbar card-toolbar-mobile'>
                        <button 
                            className='btn btn-secondary btn-mobile'
                            onClick={fetchShipments}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    <span className="btn-text">Loading...</span>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-sync-alt"></i>
                                    <span className="btn-text">Refresh</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <div className='card-body'>
                    {/* Filters */}
                    <div className='filters-container filters-container-mobile mb-4'>
                        {/* Mobile Filters */}
                        <div className='d-lg-none mobile-filters'>
                            <div className='mobile-search-wrapper mb-3'>
                                <div className="position-relative">
                                    <input
                                        type='text'
                                        className='form-control form-control-mobile-search'
                                        placeholder='Search shipments...'
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                    <div className="search-icon-mobile">
                                        <i className="fas fa-search"></i>
                                    </div>
                                    {loading && (
                                        <div className="search-loading-mobile">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                <span className="visually-hidden">Searching...</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className='mobile-filter-row'>
                                <div className='mobile-filter-item'>
                                    <label className='filter-label-mobile'>Status</label>
                                    <select
                                        className='form-select form-select-mobile'
                                        value={filterStatus}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value='all'>All Status</option>
                                        <option value='0'>Pending</option>
                                        <option value='1'>Delivered</option>
                                        <option value='2'>In Transit</option>
                                        <option value='3'>Out for Delivery</option>
                                    </select>
                                </div>
                                <div className='mobile-filter-item'>
                                    <label className='filter-label-mobile'>Show</label>
                                    <select
                                        className='form-select form-select-mobile'
                                        value={itemsPerPage}
                                        onChange={(e) => handleFilterChange('perPage', e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Desktop Filters */}
                        <div className='d-none d-lg-block'>
                            <div className='row'>
                                <div className='col-md-4'>
                                    <div className="position-relative">
                                        <input
                                            type='text'
                                            className='form-control'
                                            placeholder='Search shipments by number, order, or customer...'
                                            value={searchTerm}
                                            onChange={(e) => handleSearchChange(e.target.value)}
                                            disabled={loading}
                                        />
                                        {loading && (
                                            <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Searching...</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className='col-md-3'>
                                    <select
                                        className='form-select'
                                        value={filterStatus}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value='all'>All Status</option>
                                        <option value='0'>Pending</option>
                                        <option value='1'>Delivered</option>
                                        <option value='2'>In Transit</option>
                                        <option value='3'>Out for Delivery</option>
                                    </select>
                                </div>
                                <div className='col-md-2'>
                                    <select
                                        className='form-select'
                                        value={itemsPerPage}
                                        onChange={(e) => handleFilterChange('perPage', e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value={10}>10 per page</option>
                                        <option value={20}>20 per page</option>
                                        <option value={50}>50 per page</option>
                                        <option value={100}>100 per page</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Summary */}
                    <div className='row mb-3'>
                        <div className='col-12'>
                            <div className='text-muted small'>
                                {pagination.total > 0 ? (
                                    <>
                                        Showing {pagination.from}-{pagination.to} of {pagination.total} shipments
                                    </>
                                ) : (
                                    'No shipments found'
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Shipment List - Desktop Table / Mobile Cards */}
                    <div className='shipment-list-container'>
                        {/* Desktop Table View */}
                        <div className='d-none d-lg-block'>
                            <div className='table-responsive'>
                                <table className='table table-striped table-hover'>
                                    <thead className='table-dark'>
                                        <tr>
                                            <th>Shipment No</th>
                                            <th>Order No</th>
                                            <th>Customer</th>
                                            <th>Status</th>
                                            <th>Total Qty</th>
                                            <th>Shipment Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan='7' className='text-center py-4'>
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-2">Loading shipments...</p>
                                                </td>
                                            </tr>
                                        ) : shipments.length === 0 ? (
                                            <tr>
                                                <td colSpan='7' className='text-center py-4'>
                                                    <div className='text-muted'>
                                                        <i className='fas fa-shipping-fast fa-3x mb-3'></i>
                                                        <p>No shipments found</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            shipments.map((shipment) => (
                                                <tr key={shipment.salesShipmentId}>
                                                    <td>
                                                        <span className='badge bg-primary text-white'>
                                                            {shipment.shipmentNo}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Link to={`/sales-orders/${shipment.orderId}`} className='text-decoration-none'>
                                                            {shipment.orderNo}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <strong>{shipment.customerName}</strong>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadge(shipment.status)}`}>
                                                            {ShipmentService.formatShipmentStatus(shipment.status).label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className='badge bg-info'>
                                                            {parseInt(shipment.totalQty) || 0}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(shipment.shipmentDate)}</td>
                                                    <td>
                                                        <div className='btn-group btn-group-sm'>
                                                            <Link
                                                                to={`/shipments/${shipment.salesShipmentId}`}
                                                                className='btn btn-outline-primary'
                                                                title='View Details'
                                                            >
                                                                <i className='fas fa-eye'></i>
                                                            </Link>
                                                            <button
                                                                className='btn btn-outline-secondary'
                                                                onClick={() => handlePrintShipment(shipment.salesShipmentId)}
                                                                title='Print Shipment'
                                                            >
                                                                <i className='fas fa-print'></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile Card View */}
                        <div className='d-lg-none'>
                            {loading ? (
                                <div className='text-center py-5'>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2">Loading shipments...</p>
                                </div>
                            ) : shipments.length === 0 ? (
                                <div className='text-center py-5'>
                                    <div className='text-muted'>
                                        <i className='fas fa-shipping-fast fa-3x mb-3'></i>
                                        <p>No shipments found</p>
                                    </div>
                                </div>
                            ) : (
                                <div className='shipment-cards'>
                                    {shipments.map((shipment) => (
                                        <div 
                                            key={shipment.salesShipmentId} 
                                            className='shipment-card'
                                        >
                                            <div className='shipment-card-header'>
                                                <div className='shipment-number'>
                                                    <strong>{shipment.shipmentNo}</strong>
                                                    <div className='shipment-order'>
                                                        <small className='text-muted'>Order: {shipment.orderNo}</small>
                                                    </div>
                                                </div>
                                                <div className='shipment-status'>
                                                    <span className={`badge ${getStatusBadge(shipment.status)}`}>
                                                        {ShipmentService.formatShipmentStatus(shipment.status).label}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className='shipment-card-body'>
                                                <div className='shipment-info-row'>
                                                    <span className='info-label-mobile'>
                                                        <i className='fas fa-user me-2'></i>Customer:
                                                    </span>
                                                    <span className='info-value-mobile'>
                                                        {shipment.customerName}
                                                    </span>
                                                </div>
                                                
                                                <div className='shipment-info-row'>
                                                    <span className='info-label-mobile'>
                                                        <i className='fas fa-boxes me-2'></i>Total Qty:
                                                    </span>
                                                    <span className='info-value-mobile'>
                                                        <span className='badge bg-info'>
                                                            {parseInt(shipment.totalQty) || 0}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className='shipment-card-footer'>
                                                <div className='shipment-date'>
                                                    <i className='fas fa-calendar me-2 text-muted'></i>
                                                    <small className='text-muted'>
                                                        {formatDate(shipment.shipmentDate)}
                                                    </small>
                                                </div>
                                                <div className='shipment-actions'>
                                                    <Link
                                                        to={`/shipments/${shipment.salesShipmentId}`}
                                                        className='btn btn-sm btn-outline-primary me-1'
                                                        title='View Details'
                                                    >
                                                        <i className='fas fa-eye'></i>
                                                    </Link>
                                                    <button
                                                        className='btn btn-sm btn-outline-secondary'
                                                        onClick={() => handlePrintShipment(shipment.salesShipmentId)}
                                                        title='Print Shipment'
                                                    >
                                                        <i className='fas fa-print'></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                        <div className='d-flex justify-content-between align-items-center mt-4'>
                            <div className='text-muted small'>
                                Page {pagination.current_page} of {pagination.total_pages}
                            </div>
                            <nav>
                                <ul className='pagination mb-0'>
                                    <li className={`page-item ${!pagination.has_prev_page ? 'disabled' : ''}`}>
                                        <button
                                            className='page-link'
                                            onClick={() => handlePageChange(1)}
                                            disabled={!pagination.has_prev_page}
                                        >
                                            <i className="fas fa-angle-double-left"></i>
                                        </button>
                                    </li>
                                    <li className={`page-item ${!pagination.has_prev_page ? 'disabled' : ''}`}>
                                        <button
                                            className='page-link'
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={!pagination.has_prev_page}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    <li className={`page-item ${!pagination.has_next_page ? 'disabled' : ''}`}>
                                        <button
                                            className='page-link'
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={!pagination.has_next_page}
                                        >
                                            Next
                                        </button>
                                    </li>
                                    <li className={`page-item ${!pagination.has_next_page ? 'disabled' : ''}`}>
                                        <button
                                            className='page-link'
                                            onClick={() => handlePageChange(pagination.total_pages)}
                                            disabled={!pagination.has_next_page}
                                        >
                                            <i className="fas fa-angle-double-right"></i>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                /* Shipment Cards */
                .shipment-cards {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
                
                .shipment-card {
                    background: white;
                    border: 1px solid #e9ecef;
                    border-radius: 0.75rem;
                    padding: 1rem;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .shipment-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    transform: translateY(-1px);
                    border-color: #007bff;
                }
                
                .shipment-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 0.75rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px solid #f1f3f4;
                }
                
                .shipment-number {
                    flex: 1;
                }
                
                .shipment-number strong {
                    font-size: 1.1rem;
                    color: #212529;
                    display: block;
                    margin-bottom: 0.25rem;
                }

                .shipment-order {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .shipment-order .badge {
                    font-size: 0.75rem;
                    align-self: flex-start;
                }
                
                .shipment-status .badge {
                    font-size: 0.75rem;
                    padding: 0.375rem 0.75rem;
                }
                
                .shipment-card-body {
                    margin-bottom: 0.75rem;
                }
                
                .shipment-info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                    min-height: 1.5rem;
                }
                
                .shipment-info-row:last-child {
                    margin-bottom: 0;
                }
                
                .info-label-mobile {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #6c757d;
                    flex-shrink: 0;
                    min-width: fit-content;
                    display: flex;
                    align-items: center;
                }
                
                .info-value-mobile {
                    font-size: 0.85rem;
                    color: #212529;
                    font-weight: 500;
                    text-align: right;
                    flex: 1;
                    margin-left: 0.5rem;
                    word-break: break-word;
                }
                
                .shipment-card-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-top: 0.75rem;
                    border-top: 1px solid #f1f3f4;
                }
                
                .shipment-date {
                    display: flex;
                    align-items: center;
                    flex: 1;
                }
                
                .shipment-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                
                .shipment-actions .btn {
                    width: 2.25rem;
                    height: 2.25rem;
                    padding: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Enhanced Card Header for Mobile */
                .card-header-mobile {
                    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                    border-bottom: 1px solid #e9ecef;
                    border-radius: 0.75rem 0.75rem 0 0 !important;
                    padding: 1rem 1.5rem;
                    position: relative;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .card-title .card-label {
                    color: #212529;
                    font-weight: 600;
                    margin: 0;
                    font-size: 1.25rem;
                }

                .card-toolbar-mobile {
                    display: flex;
                    gap: 0.5rem;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .btn-mobile {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    border-radius: 0.5rem;
                    font-weight: 500;
                    font-size: 0.875rem;
                    padding: 0.5rem 1rem;
                    border: none;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .btn-mobile:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
                }

                .btn-mobile i {
                    font-size: 0.875rem;
                }

                /* Enhanced Mobile Filters */
                .filters-container-mobile {
                    background: #f8f9fa;
                    border-radius: 0.75rem;
                    padding: 1rem;
                    border: 1px solid #e9ecef;
                }

                .mobile-filters {
                    width: 100%;
                }

                .mobile-search-wrapper {
                    width: 100%;
                }

                .form-control-mobile-search {
                    height: 3rem;
                    border-radius: 1.5rem;
                    border: 2px solid #e9ecef;
                    padding-left: 3rem;
                    padding-right: 1rem;
                    font-size: 1rem;
                    background: white;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    font-weight: 400;
                }

                .form-control-mobile-search:focus {
                    border-color: #007bff;
                    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
                    transform: translateY(-1px);
                    outline: none;
                }

                .search-icon-mobile {
                    position: absolute;
                    left: 1rem;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #6c757d;
                    font-size: 1.1rem;
                    pointer-events: none;
                }

                .search-loading-mobile {
                    position: absolute;
                    right: 1rem;
                    top: 50%;
                    transform: translateY(-50%);
                }

                .mobile-filter-row {
                    display: flex;
                    gap: 0.75rem;
                    width: 100%;
                }

                .mobile-filter-item {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .filter-label-mobile {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 0;
                }

                .form-select-mobile {
                    height: 2.75rem;
                    border-radius: 0.75rem;
                    border: 2px solid #e9ecef;
                    background: white;
                    font-size: 0.875rem;
                    font-weight: 500;
                    padding: 0.5rem 0.75rem;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                }

                .form-select-mobile:focus {
                    border-color: #007bff;
                    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
                    outline: none;
                }

                /* Mobile Results Summary Enhancement */
                .text-muted.small {
                    background: rgba(108, 117, 125, 0.1);
                    padding: 0.75rem 1rem;
                    border-radius: 0.5rem;
                    font-weight: 500;
                    text-align: center;
                    font-size: 0.875rem;
                    color: #495057;
                    border: 1px solid rgba(108, 117, 125, 0.1);
                }

                /* Mobile responsive adjustments */
                @media (max-width: 768px) {
                    /* Card Header Mobile */
                    .card-header-mobile {
                        padding: 1rem;
                        flex-direction: column;
                        align-items: stretch;
                        gap: 1rem;
                    }

                    .card-toolbar-mobile {
                        width: 100%;
                        justify-content: stretch;
                        gap: 0.5rem;
                    }

                    .btn-mobile {
                        flex: 1;
                        justify-content: center;
                        padding: 0.75rem 1rem;
                        font-size: 0.875rem;
                        min-height: 2.5rem;
                    }

                    .btn-text {
                        display: inline;
                    }

                    /* Filter Container Mobile */
                    .filters-container-mobile {
                        padding: 0.75rem;
                        margin: 0 -0.25rem 1rem -0.25rem;
                    }

                    .mobile-search-wrapper {
                        margin-bottom: 1rem;
                    }

                    .form-control-mobile-search {
                        height: 2.75rem;
                        font-size: 0.95rem;
                        padding-left: 2.75rem;
                    }

                    .search-icon-mobile {
                        left: 0.875rem;
                        font-size: 1rem;
                    }

                    .mobile-filter-row {
                        gap: 0.5rem;
                    }

                    .filter-label-mobile {
                        font-size: 0.8rem;
                    }

                    .form-select-mobile {
                        height: 2.5rem;
                        font-size: 0.8rem;
                        padding: 0.375rem 0.625rem;
                    }

                    /* Results Summary Mobile */
                    .text-muted.small {
                        padding: 0.5rem 0.75rem;
                        font-size: 0.8rem;
                        margin: 0 -0.25rem 1rem -0.25rem;
                    }
                }

                @media (max-width: 576px) {
                    /* Extra Small Mobile */
                    .card-header-mobile {
                        padding: 0.75rem;
                        border-radius: 0.5rem 0.5rem 0 0 !important;
                    }

                    .card-title .card-label {
                        font-size: 1.1rem;
                        text-align: center;
                    }

                    .btn-mobile {
                        padding: 0.625rem 0.75rem;
                        font-size: 0.8rem;
                        min-height: 2.25rem;
                    }

                    .btn-mobile .btn-text {
                        font-size: 0.8rem;
                    }

                    .filters-container-mobile {
                        padding: 0.5rem;
                        border-radius: 0.5rem;
                    }

                    .form-control-mobile-search {
                        height: 2.5rem;
                        font-size: 0.9rem;
                        padding-left: 2.5rem;
                        border-radius: 1.25rem;
                    }

                    .search-icon-mobile {
                        left: 0.75rem;
                        font-size: 0.9rem;
                    }

                    .mobile-filter-row {
                        flex-direction: column;
                        gap: 0.75rem;
                    }

                    .mobile-filter-item {
                        width: 100%;
                    }

                    .filter-label-mobile {
                        font-size: 0.75rem;
                    }

                    .form-select-mobile {
                        height: 2.25rem;
                        font-size: 0.75rem;
                        border-radius: 0.5rem;
                    }

                    .text-muted.small {
                        padding: 0.5rem;
                        font-size: 0.75rem;
                        border-radius: 0.375rem;
                    }

                    .shipment-card {
                        padding: 0.75rem;
                        border-radius: 0.5rem;
                    }
                    
                    .shipment-number strong {
                        font-size: 1rem;
                    }
                    
                    .info-label-mobile {
                        font-size: 0.75rem;
                    }
                    
                    .info-value-mobile {
                        font-size: 0.8rem;
                    }
                    
                    .shipment-info-row {
                        margin-bottom: 0.375rem;
                    }

                    .shipment-actions .btn {
                        width: 2rem;
                        height: 2rem;
                        font-size: 0.8rem;
                    }
                }
            `}</style>
        </>
    )
}

export default ShipmentList 